import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  of,
  from,
  concat,
  concatMap,
  withLatestFrom,
  map,
  catchError,
  filter,
  exhaustMap,
  tap,
  switchMap,
} from 'rxjs';

import { StripeService } from '../../services/stripe.service';
import {
  CustomerEnvelope,
  SetupIntentEnvelope,
  SubscriptionEnvelope,
  CustomerResponse,
  SetupIntentResponse,
  SubscriptionResponse,
  DonationMetadata,
  SetupIntentRequest,
  SubscriptionRequest,
  CustomerRequest,
  InvoiceEnvelope,
  InvoiceRequest,
  StripeInvoiceLite,
} from '../../types/stripe-models';

import {
  DonationFlowStripeActions,
  DonationFlowHelpersActions,
  DonationFlowUiActions,
  showFeeReview,
  acceptFeeReview,
  useAnotherCard,
  cancelFeeReview,
} from './actions';
import {
  selectPriceDraft,
  selectCustomer,
  selectAutoFlowModePopup,
  selectAutoFlowModeAll,
  selectPaymentMethodId,
  // selectPaymentIntent,
  selectFeesCovered,
  selectDonationType,
  selectDonationMetadata,
  selectInvoiceAmounts,
  selectSubscriptionAmounts,
  selectRestartCustomerRequest,
  selectDeferFirstCharge,
  selectFirstPaymentDate,
} from './selectors';
import { CampaignsActions } from '../campaing/actions';
import { StripeElementsService } from '../../services/stripe-elements.service';
import {
  PriceDraft,
  DonationType,
  calculateFeeMinor,
} from '../../types/donation-flow.models';
import { getDonationStepPath } from '../../types/donation-routes';
import { Router } from '@angular/router';
import { OneTimePaymentActions, RecurringPaymentActions } from '../payment/actions';

@Injectable()
export class DonationFlowEffects {
  private actions$ = inject(Actions);
  private stripe = inject(StripeService);
  private store = inject(Store);
  private router = inject(Router);

  private stripeElements = inject(StripeElementsService);

  private isCustomDonationContext(): boolean {
    const tree = this.router.parseUrl(this.router.url);
    const primary = tree.root.children['primary']?.segments?.map((s) => s.path) ?? [];
    return primary[0] === 'donation-custom';
  }

  private wrapOk<T>(p: Promise<T>) {
    return from(p).pipe(
      map((res: T) => ({ ok: true as const, res })),
      catchError((error) => of({ ok: false as const, error }))
    );
  }

  /**
   * Build absolute URLs for the current navigation context (modal outlet vs
   * `/donation-custom`). Segments come from `getDonationStepPath` so no
   * effect concatenates `/one-time/step/2` by hand.
   */
  private buildDonationStepUrl(
    donationType: DonationType,
    step: number
  ): string {
    const [type, seg, n] = getDonationStepPath(donationType, step);
    if (this.isCustomDonationContext()) {
      return `/donation-custom/${type}/${seg}/${n}`;
    }
    return `/(modal:donation-modal/${type}/${seg}/${n})`;
  }

  private paymentStepRoute(donationType: DonationType) {
    return this.buildDonationStepUrl(donationType, 2);
  }

  private recurringConfirmStepRoute() {
    return this.buildDonationStepUrl(DonationType.Recurring, 3);
  }

  private recurringThanksStepRoute() {
    return this.buildDonationStepUrl(DonationType.Recurring, 4);
  }

  private oneTimeThanksStepRoute() {
    return this.buildDonationStepUrl(DonationType.OneTime, 3);
  }

  /** ONE-TIME: Step 2 -> Customer -> SetupIntent(Create) -> (UI monta Payment Elements en success)
   *   - SIN Price: unitAmount / currency salen de priceDraft
   */
  beginStep2OneTime$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DonationFlowUiActions.beginStep2OneTime),
      withLatestFrom(
        this.store.select(selectPriceDraft),
        this.store.select(selectDonationMetadata)
      ),
      concatMap(
        ([action, priceDraft, metadata]: [
          ReturnType<typeof DonationFlowUiActions.beginStep2OneTime>,
          PriceDraft | null,
          DonationMetadata | null
        ]) => {
          // Validaciones mínimas: necesitamos monto/moneda y metadata para continuar
          if (!priceDraft || !metadata) {
            return of(
              DonationFlowStripeActions.sendPriceFailure({
                error: 'Missing price draft or metadata',
              })
            );
          }

          if (!priceDraft.unitAmount || !priceDraft.currency) {
            return of(
              DonationFlowStripeActions.sendPriceFailure({
                error: 'Incomplete price draft (currency/unitAmount).',
              })
            );
          }

          // 1) Customer request (único paralelo "real" ahora)
          const customerReq: CustomerRequest = {
            ...action.customer,
            action: 'Create',
          };

          // Enciende loading (customer)
          const startLoading = of(
            DonationFlowStripeActions.sendCustomer({ request: customerReq })
          );

          const custEnv: CustomerEnvelope = {
            type: 'Customer',
            data: customerReq,
          };

          // 2) Ejecutar Customer y, si OK, crear SetupIntent para montar Payment Elements en Step 3
          const joined$ = from(
            this.stripe.send(custEnv) as Promise<CustomerResponse>
          ).pipe(
            concatMap((customerRes) => {
              // Emitimos success de Customer
              const updates = of(
                DonationFlowStripeActions.sendCustomerSuccess({
                  response: customerRes,
                })
              );

              // Crear SetupIntent (igual que en recurring: UI monta Elements al recibir el success)
              const siReq: SetupIntentRequest = {
                action: 'Create',
                customerId: customerRes.id,
                // Si tu servicio lo soporta, el backend puede fijar usage='off_session'
                metadata, // pasamos metadata tal cual
              };

              const siEnv: SetupIntentEnvelope = {
                type: 'SetupIntent',
                data: siReq,
              };

              const startSi = of(
                DonationFlowStripeActions.sendSetupIntent({ request: siReq })
              );

              const callSi = from(
                this.stripe.send(siEnv) as Promise<SetupIntentResponse>
              ).pipe(
                map((si) =>
                  DonationFlowStripeActions.sendSetupIntentSuccess({
                    response: si,
                  })
                ),
                catchError((error) =>
                  of(
                    DonationFlowStripeActions.sendSetupIntentFailure({ error })
                  )
                )
              );

              return concat(updates, startSi, callSi);
            }),
            catchError((error) =>
              of(DonationFlowStripeActions.sendCustomerFailure({ error }))
            )
          );

          return concat(startLoading, joined$);
        }
      )
    )
  );

  /** ONE-TIME: tras confirmar SetupIntent en cliente, crear el Invoice y auto-cobrar
   *   - SIN Price: tomamos amount/currency de priceDraft
   */
  autoCreateInvoiceAfterSetupConfirm$ = createEffect(() =>
    this.actions$.pipe(
      // Dispara cuando hidratamos el SI confirmado o cuando seteamos pmId
      ofType(
        DonationFlowUiActions.hydrateSetupIntentAfterClientConfirm,
        DonationFlowHelpersActions.setPaymentMethodIdFromConfirm
      ),
      withLatestFrom(
        this.store.select(selectDonationType),
        this.store.select(selectCustomer),
        this.store.select(selectPaymentMethodId),
        this.store.select(selectInvoiceAmounts),
        this.store.select(selectDonationMetadata),
        this.store.select(selectPriceDraft)
      ),

      // Solo cuando es one-time y hay todo lo necesario (sin price)
      filter(
        ([
          ,
          donationType,
          customer,
          pmId,
          invoiceSummary,
          metadata,
          priceDraft,
        ]) =>
          donationType === DonationType.OneTime &&
          !!customer?.id &&
          !!pmId &&
          !!invoiceSummary &&
          !!metadata &&
          !!priceDraft?.unitAmount &&
          !!priceDraft?.currency
      ),

      // Evita dobles disparos simultáneos si llegan ambos eventos a la vez
      exhaustMap(
        ([, , customer, pmId, invoiceSummary, metadata, priceDraft]) => {
          if (!metadata || !priceDraft?.unitAmount || !priceDraft?.currency) {
            return of(
              DonationFlowStripeActions.sendPriceFailure({
                error: 'Missing required data (metadata/priceDraft).',
              })
            );
          }

          const setLoading = DonationFlowUiActions.setFeeCalculationStatus({ status: 'loading' });

          const calcFee$ = from(this.stripe.getCardFeeType(pmId!)).pipe(
            switchMap((cardFeeType) => {
              const setFeeType = DonationFlowUiActions.setCardFeeType({
                cardFeeType,
              });

              const donationType = metadata.donationType;
              if (!donationType) {
                return of(
                  DonationFlowUiActions.setFeeCalculationStatus({ status: 'error' }),
                  DonationFlowStripeActions.sendInvoiceFailure({
                    error: 'Missing donationType. Cannot proceed to summary.',
                  })
                );
              }

              const baseMinor = invoiceSummary.baseMinor;
              const feeMinor = calculateFeeMinor(baseMinor, cardFeeType, donationType);

              return of(
                setFeeType,
                showFeeReview({
                  visible: true,
                  feeType: cardFeeType,
                  currency: priceDraft.currency,
                  baseMinor,
                  newFeeMinor: feeMinor,
                }),
                DonationFlowUiActions.setFeeCalculationStatus({ status: 'calculated' })
              );
            }),
            catchError((error) =>
              of(
                DonationFlowUiActions.setFeeCalculationStatus({ status: 'error' }),
                DonationFlowStripeActions.sendPriceFailure({ error })
              )
            )
          );

          return concat(of(setLoading), calcFee$);
        }
      )
    )
  );

  // ========== Aceptar review → One-time: Invoice | Recurring: Subscription ==========
  continueAfterFeeReview$ = createEffect(() =>
  this.actions$.pipe(
    ofType(acceptFeeReview),
    withLatestFrom(
      this.store.select(selectDonationType),
      this.store.select(selectDonationMetadata),
      this.store.select(selectCustomer),
      this.store.select(selectPaymentMethodId),
      this.store.select(selectPriceDraft),
      this.store.select(selectInvoiceAmounts),
      this.store.select(selectSubscriptionAmounts),
      this.store.select(selectDeferFirstCharge),
      this.store.select(selectFirstPaymentDate),
    ),
    exhaustMap(
      ([
        ,
        donationType,
        metadata,
        customer,
        pmId,
        priceDraft,
        invoiceSummary,
        subscriptionSummary,
        isFirstChargeDeferred,
        firstPaymentDate
      ]) => {
        if (
          !donationType ||
          !customer?.id ||
          !pmId ||
          !metadata ||
          !priceDraft?.currency
        ) {
          return of(
            DonationFlowStripeActions.sendPriceFailure({
              error: 'Missing data to continue after fee review accept.',
            })
          );
        }

        if (donationType === DonationType.OneTime) {
          if (!invoiceSummary) {
            return of(
              DonationFlowStripeActions.sendInvoiceFailure({
                error: 'Missing invoice summary for one-time fee review flow.',
              })
            );
          }

          const req: InvoiceRequest = {
            action: 'Create',
            paymentMethodId: pmId,
            customerId: customer.id,
            productId: metadata.productId,
            productFeeId: metadata.productFeeId,
            email: metadata.customerEmail,
            collectionMethod: 'charge_automatically',
            amount: invoiceSummary.baseMinor,
            fees: invoiceSummary.feeMinor,
            isFeesCovered: !!invoiceSummary.feesCovered,
            currency: priceDraft.currency ?? 'USD',
            metadata,
          };

          const env: InvoiceEnvelope = { type: 'Invoice', data: req };

          const start = of(
            DonationFlowStripeActions.sendInvoice({ request: req })
          );

          const call$ = from(
            this.stripe.send(env) as Promise<StripeInvoiceLite>
          ).pipe(
            map((response) =>
              DonationFlowStripeActions.sendInvoiceSuccess({ response })
            ),
            catchError((error) =>
              of(DonationFlowStripeActions.sendInvoiceFailure({ error }))
            )
          );

          return concat(start, call$);
        }

        if (!priceDraft?.productId || !subscriptionSummary?.baseMinor) {
          return of(
            DonationFlowStripeActions.sendSubscriptionFailure({
              error:
                'Missing productId or subscription amounts for recurring fee review flow.',
            })
          );
        }

        const shouldDeferFirstCharge =
          isFirstChargeDeferred === true && !!firstPaymentDate;

        const reqSub: SubscriptionRequest = {
          action: 'Create',
          metadata,
          productId: priceDraft.productId,
          isFeesCovered: !!subscriptionSummary.feesCovered,
          fees: subscriptionSummary.feeMinor,
          customerId: customer.id,
          paymentMethodId: pmId,
          amount: Number(priceDraft.unitAmount),
          isFirstChargeDeferred: shouldDeferFirstCharge,
          firstPaymentDate: shouldDeferFirstCharge ? firstPaymentDate : null,
        };

        const envSub: SubscriptionEnvelope = {
          type: 'Subscription',
          data: reqSub,
        };

        const startSub = of(
          DonationFlowStripeActions.sendSubscription({ request: reqSub })
        );

        const callSub$ = from(
          this.stripe.send(envSub) as Promise<SubscriptionResponse>
        ).pipe(
          concatMap((response) => {
            const ok = DonationFlowStripeActions.sendSubscriptionSuccess({
              response,
            });

            const pi: any = (response as any)?.latest_invoice?.payment_intent;
            const clientSecret: string | undefined = pi?.client_secret;

            if (clientSecret) {
              const confirm =
                DonationFlowHelpersActions.confirmSubscriptionFirstInvoicePayment(
                  {
                    clientSecret,
                    subscriptionId: response.id,
                  }
                );

              this.router.navigateByUrl(this.recurringConfirmStepRoute(), {
                replaceUrl: true,
              });

              return of(ok, confirm);
            }

            const finalize =
              DonationFlowHelpersActions.finalizeRecurringSuccess({
                subscriptionId: response.id,
              });

            return of(ok, finalize);
          }),
          catchError((error) =>
            of(DonationFlowStripeActions.sendSubscriptionFailure({ error }))
          )
        );

        return concat(startSub, callSub$);
      }
    )
  )
); 

  // Use another card => volver al paso de pago (y limpiar PM si aplica) con reset
  restartPaymentFlowAfterUseAnotherCard$ = createEffect(() =>
  this.actions$.pipe(
    ofType(useAnotherCard),
    withLatestFrom(
      this.store.select(selectDonationMetadata),
      this.store.select(selectRestartCustomerRequest),
      this.store.select(selectDonationType)
    ),
    concatMap(([, metadata, customerRequest, donationType]) => {
      const resetActions =
        donationType === DonationType.Recurring
          ? [
              RecurringPaymentActions.viewExited(),
              RecurringPaymentActions.setClientSecret({ clientSecret: null }),
              RecurringPaymentActions.setActive({ active: false }),
            ]
          : [
              OneTimePaymentActions.viewExited(),
              OneTimePaymentActions.setClientSecret({ clientSecret: null }),
              OneTimePaymentActions.setActive({ active: false }),
            ];

      if (!metadata || !customerRequest) {
        return of(
          DonationFlowUiActions.resetCardBrandState(),
          ...resetActions
        );
      }

      const restartAction =
        donationType === DonationType.Recurring
          ? DonationFlowUiActions.beginStep2Recurring({
              customer: customerRequest,
            })
          : DonationFlowUiActions.beginStep2OneTime({
              customer: customerRequest,
            });

      return concat(
        of(DonationFlowUiActions.resetCardBrandState()),
        of(...resetActions),
        of(restartAction)
      );
    })
  )
);

  // Cancelar review => cerrar modal y navegar si quieres with reset
  resetCardBrandOnFeeReviewCancel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cancelFeeReview),
      map(() => DonationFlowUiActions.resetCardBrandState())
    )
  );

  feeReviewCancel$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(cancelFeeReview),
        withLatestFrom(this.store.select(selectDonationType)),
        tap(([, donationType]) => {
          if (!donationType) return;
          this.router.navigateByUrl(this.paymentStepRoute(donationType), {
            replaceUrl: true,
          });
        })
      ),
    { dispatch: false }
  );

  /** RECURRING: Step 2 -> Customer + Price (parallel) -> SetupIntent(Create) */
  /** RECURRING: Step 2 -> Customer + Price (parallel) -> SetupIntent(Create) */
  beginStep2Recurring$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DonationFlowUiActions.beginStep2Recurring),
      withLatestFrom(this.store.select(selectDonationMetadata)),
      concatMap(
        ([action, metadata]: [
          ReturnType<typeof DonationFlowUiActions.beginStep2Recurring>,
          DonationMetadata | null
        ]) => {
          if (!metadata) {
            return of(
              DonationFlowStripeActions.sendCustomerFailure({
                error: 'Missing Metadata',
              })
            );
          }

          const customerReq: CustomerRequest = {
            ...action.customer,
            action: 'Create',
          };

          // loading state para customer
          const startLoading = of(
            DonationFlowStripeActions.sendCustomer({ request: customerReq })
          );

          const custEnv: CustomerEnvelope = {
            type: 'Customer',
            data: customerReq,
          };

          const joined$ = from(this.stripe.send(custEnv)).pipe(
            concatMap((customer) => {
              const updates: any[] = [];

              updates.push(
                DonationFlowStripeActions.sendCustomerSuccess({
                  response: customer,
                })
              );

              // si el customer fue creado, arrancamos SetupIntent
              const siReq: SetupIntentRequest = {
                action: 'Create',
                customerId: customer.id,
                metadata,
              };
              const siEnv: SetupIntentEnvelope = {
                type: 'SetupIntent',
                data: siReq,
              };

              const startSi = of(
                DonationFlowStripeActions.sendSetupIntent({ request: siReq })
              );
              const callSi = from(this.stripe.send(siEnv)).pipe(
                map((si) =>
                  DonationFlowStripeActions.sendSetupIntentSuccess({
                    response: si,
                  })
                ),
                catchError((error) =>
                  of(
                    DonationFlowStripeActions.sendSetupIntentFailure({
                      error,
                    })
                  )
                )
              );

              return concat(of(...updates), startSi, callSi);
            }),
            catchError((error) =>
              of(DonationFlowStripeActions.sendCustomerFailure({ error }))
            )
          );

          return concat(startLoading, joined$);
        }
      )
    )
  );

  // ========== 4.1 Crear SUB cuando ya hay customer + priceDraft + paymentMethodId + setupIntent:succeeded ==========
  autoCreateSubscriptionAfterSetupConfirm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DonationFlowUiActions.hydrateSetupIntentAfterClientConfirm,
        DonationFlowHelpersActions.setPaymentMethodIdFromConfirm
      ),
      withLatestFrom(
        this.store.select(selectCustomer),
        this.store.select(selectPriceDraft),
        this.store.select(selectPaymentMethodId),
        this.store.select(selectFeesCovered),
        this.store.select(selectSubscriptionAmounts),
        this.store.select(selectDonationMetadata),
      ),
      filter(
        ([, customer, priceDraft, pm, , donationSummary, metadata]) =>
          donationSummary.donationType === DonationType.Recurring &&
          !!customer?.id &&
          !!pm &&
          !!priceDraft?.unitAmount &&
          !!priceDraft?.currency &&
          !!priceDraft?.productId &&
          !!metadata
      ),
      exhaustMap(
        ([
          ,
          customer,
          priceDraft,
          pm,
          ,
          donationSummary,
          metadata,
        ]) => {
          if (
            !priceDraft?.unitAmount ||
            !priceDraft?.currency ||
            !priceDraft?.productId ||
            !metadata ||
            !donationSummary
          ) {
            return of(
              DonationFlowStripeActions.sendSubscriptionFailure({
                error:
                  'Incomplete price draft (unitAmount/currency/productId/valueData).',
              })
            );
          }

          const setLoading = DonationFlowUiActions.setFeeCalculationStatus({ status: 'loading' });

          const calcFee$ = from(this.stripe.getCardFeeType(pm!)).pipe(
            switchMap((cardFeeType) => {
              const setFeeType = DonationFlowUiActions.setCardFeeType({
                cardFeeType,
              });

              const donationType = donationSummary.donationType;
              if (!donationType) {
                return of(
                  DonationFlowUiActions.setFeeCalculationStatus({ status: 'error' }),
                  DonationFlowStripeActions.sendSubscriptionFailure({
                    error: 'Missing donationType. Cannot proceed to summary.',
                  })
                );
              }

              const baseMinor = donationSummary.baseMinor;
              const feeMinor = calculateFeeMinor(baseMinor, cardFeeType, donationType);

              return of(
                setFeeType,
                showFeeReview({
                  visible: true,
                  feeType: cardFeeType,
                  currency: priceDraft.currency,
                  baseMinor,
                  newFeeMinor: feeMinor,
                }),
                DonationFlowUiActions.setFeeCalculationStatus({ status: 'calculated' })
              );
            }),
            catchError((error) =>
              of(
                DonationFlowUiActions.setFeeCalculationStatus({ status: 'error' }),
                DonationFlowStripeActions.sendSubscriptionFailure({ error })
              )
            )
          );

          return concat(of(setLoading), calcFee$);
        }
      )
    )
  );

  // ========== 4.2 Confirmar el PaymentIntent del primer invoice (SCA-friendly) ==========
  confirmInvoicePayment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DonationFlowHelpersActions.confirmSubscriptionFirstInvoicePayment),
      concatMap(({ clientSecret, subscriptionId }) => {
        const start = of(); // si quieres setear algún loading, agrégalo
        const call$ = from(
          this.stripeElements.confirmInvoicePayment(clientSecret)
        ).pipe(
          concatMap((res: any) => {
            const pi = res?.paymentIntent ?? res;
            const status = pi?.status;
            if (status === 'succeeded' || status === 'processing') {
              const ok =
                DonationFlowHelpersActions.confirmInvoicePaymentSuccess({
                  paymentIntent: pi,
                });
              // refresca la SUB para ver si ya quedó ACTIVE
              const refresh = DonationFlowHelpersActions.retrieveSubscription({
                id: subscriptionId,
              });
              return of(ok, refresh);
            }
            return of(
              DonationFlowHelpersActions.confirmInvoicePaymentFailure({
                error: `Invoice PI not succeeded: ${status ?? 'unknown'}`,
              })
            );
          }),
          catchError((error) =>
            of(
              DonationFlowHelpersActions.confirmInvoicePaymentFailure({ error })
            )
          )
        );
        return concat(start, call$);
      })
    )
  );

  // ========== 4.3 Retrieve SUB y finalizar cuando está ACTIVE ==========
  retrieveAndFinalizeSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DonationFlowHelpersActions.retrieveSubscription),
      concatMap(({ id }) => {
        const env: SubscriptionEnvelope = {
          type: 'Subscription',
          data: { action: 'Retrieve', id } as any,
        };
        const start = of(
          DonationFlowStripeActions.sendSubscription({
            request: env.data as any,
          })
        );
        const call$ = from(
          this.stripe.send(env) as Promise<SubscriptionResponse>
        ).pipe(
          concatMap((response) => {
            const updated =
              DonationFlowHelpersActions.retrieveSubscriptionSuccess({
                response,
              });

            const status = (response as any)?.status;
            if (status === 'active') {
              const finalize =
                DonationFlowHelpersActions.finalizeRecurringSuccess({
                  subscriptionId: response.id,
                });
              return of(updated, finalize);
            }
            return of(updated);
          }),
          catchError((error) =>
            of(
              DonationFlowHelpersActions.retrieveSubscriptionFailure({ error })
            )
          )
        );
        return concat(start, call$);
      })
    )
  );

  navigateToRecurringThanks$ = createEffect(
  () =>
    this.actions$.pipe(
      ofType(DonationFlowHelpersActions.finalizeRecurringSuccess),
      tap(() =>
        this.store.dispatch(DonationFlowUiActions.setStep({ step: 4 }))
      ),
      tap(() =>
        this.router.navigateByUrl(
          this.recurringThanksStepRoute(),
          { replaceUrl: true }
        )
      )
    ),
  { dispatch: false }
);

  /** ONE-TIME: al crear/cobrar el invoice correctamente → pasar a Gracias (step 3) */
  navigateToOneTimeThanks$ = createEffect(
  () =>
    this.actions$.pipe(
      ofType(DonationFlowStripeActions.sendInvoiceSuccess),
      tap(() =>
        this.store.dispatch(DonationFlowUiActions.setStep({ step: 3 }))
      ),
      tap(() =>
        this.router.navigateByUrl(this.oneTimeThanksStepRoute(), {
          replaceUrl: true,
        })
      )
    ),
  { dispatch: false }
);

  /** FlowMode auto (single|multiple) a partir de campañas */
  setFlowModeAuto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignsActions.loadSuccess),
      withLatestFrom(
        this.store.select(selectAutoFlowModePopup),
        this.store.select(selectAutoFlowModeAll)
      ),
      map(([, popupMode, allMode]) => popupMode ?? allMode),
      filter((m): m is Exclude<typeof m, null> => m !== null),
      map((m) =>
        DonationFlowUiActions.setFlowMode({ flowMode: m, source: 'auto' })
      )
    )
  );
}
